"use client";

import {
  forwardRef,
  useEffect,
  useState,
  createContext,
  useContext,
} from "react";
import { cn } from "~/lib/utils";
import { CheckCircleIcon, XCircleIcon, InfoIcon, XIcon } from "~/components/icons";

type ToastVariant = "success" | "error" | "info";

interface Toast {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration?: number;
}

interface ToastContextValue {
  showToast: (
    title: string,
    variant: ToastVariant,
    options?: { description?: string; duration?: number }
  ) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = (
    title: string,
    variant: ToastVariant,
    options?: { description?: string; duration?: number }
  ) => {
    const id = Math.random().toString(36).substring(7);
    const duration = options?.duration ?? 5000;
    const newToast: Toast = {
      id,
      title,
      description: options?.description,
      variant,
      duration,
    };
    setToasts((prev) => [...prev, newToast]);

    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
      }, duration);
    }
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-3">
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            title={toast.title}
            description={toast.description}
            variant={toast.variant}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

interface ToastItemProps {
  title: string;
  description?: string;
  variant: ToastVariant;
  onClose: () => void;
}

const ToastItem = forwardRef<HTMLDivElement, ToastItemProps>(
  ({ title, description, variant, onClose }, ref) => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
      // Trigger animation after mount
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    }, []);

    const handleClose = () => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    };

    const variantConfig = {
      success: {
        iconBg: "bg-[#e4f6f1]",
        iconColor: "text-[#397367]",
        IconComponent: CheckCircleIcon,
      },
      error: {
        iconBg: "bg-[#fde8e8]",
        iconColor: "text-[#b91c1c]",
        IconComponent: XCircleIcon,
      },
      info: {
        iconBg: "bg-[#e0f2fe]",
        iconColor: "text-[#0369a1]",
        IconComponent: InfoIcon,
      },
    };

    const config = variantConfig[variant];
    const Icon = config.IconComponent;

    return (
      <div
        ref={ref}
        className={cn(
          "relative flex min-w-[320px] max-w-md gap-4 overflow-hidden rounded-xl border border-[rgba(0,0,45,0.09)] bg-white p-4 shadow-lg transition-all duration-300",
          isVisible
            ? "translate-x-0 opacity-100"
            : "translate-x-full opacity-0"
        )}
        role="alert"
        aria-live="polite"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-2 top-2 flex h-10 w-10 items-center justify-center rounded-md text-[#60646c] transition-colors hover:bg-black/5"
          aria-label="Close notification"
        >
          <XIcon className="h-[18px] w-[18px]" />
        </button>

        {/* Content */}
        <div className="flex flex-1 gap-4 pr-8">
          <div className="flex flex-1 flex-col gap-3">
            {/* Title with icon */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <div
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                    config.iconBg
                  )}
                >
                  <Icon className={cn("h-4 w-4", config.iconColor)} />
                </div>
                <p className="text-sm font-medium leading-5 text-[#1c2024]">
                  {title}
                </p>
              </div>
              {/* Description */}
              {description && (
                <div className="pl-6">
                  <p className="text-sm leading-5 text-[#60646c]">
                    {description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ToastItem.displayName = "ToastItem";
